import { useAuth0 } from '@auth0/auth0-react';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { AppTab, useNavStore } from '../../state/nav.store';
import { NavbarItems } from './Navbar';

interface MobileMenuProps {
  navbarItems: NavbarItems;
}

export default function MobileMenu({ navbarItems }: MobileMenuProps): ReactElement {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, logout } = useAuth0();

  const handleDrawerToggle = (): void => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavClick = (navbarItem: AppTab, path: string): void => {
    useNavStore.setState({ currentTab: navbarItem });
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogout = (): void => {
    void logout({ logoutParams: { localOnly: true } });
    setMobileOpen(false);
  };

  const userMenuItems = [
    {
      label: t('navbar.home'),
      onClick: () => handleNavClick('Home', '/home'),
    },
    {
      label: t('userMenu.logout'),
      onClick: handleLogout,
    },
  ];

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Link to="/" style={{ textDecoration: 'none' }}>
        <Typography variant="h6" sx={{ my: 2, fontWeight: 700, color: theme.palette.primary.main }}>
          AltonMed
        </Typography>
      </Link>
      <List>
        {(Object.keys(navbarItems) as AppTab[]).map((navbarItem) => (
          <ListItem key={navbarItem} disablePadding>
            <ListItemButton
              sx={{ textAlign: 'center' }}
              onClick={() => handleNavClick(navbarItem, navbarItems[navbarItem]!.urls?.[0])}
            >
              <ListItemText
                primary={
                  navbarItem === 'Home'
                    ? t('navbar.home')
                    : navbarItem === 'In Person'
                      ? t('navbar.inPerson')
                      : navbarItem === 'Schedules'
                        ? t('navbar.schedules')
                        : navbarItem === 'Patients'
                          ? t('navbar.patients')
                          : navbarItem === 'Employees'
                            ? t('navbar.employees')
                            : navbarItem === 'Telemedicine'
                              ? t('navbar.telemedicine')
                              : navbarItem === 'Tasks'
                                ? t('navbar.tasks')
                                : navbarItem === 'Admin'
                                  ? t('navbar.admin')
                                  : navbarItem
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      {isAuthenticated && (
        <List>
          {userMenuItems.map((item) => (
            <ListItem key={item.label} disablePadding>
              <ListItemButton sx={{ textAlign: 'center' }} onClick={item.onClick}>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    color: item.label === t('userMenu.logout') ? 'primary' : 'inherit',
                    fontWeight: item.label === t('userMenu.logout') ? 'bold' : 'normal',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );

  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
      <IconButton onClick={handleDrawerToggle}>
        <MenuIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
      </IconButton>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
}
